import {
  Box,
  Grid,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React, { FC, useMemo } from 'react';
import { reportChartPaperSx } from './reportChartLayout';
import {
  resolvedOrganizationLabel,
  type ConsolidatedComparisonMode,
  type OrgRevenueRow,
} from './ConsolidatedRevenueByOrgTable';
import { formatCurrencyWithSymbol } from 'utils/hooks/getCurrency';

const OTHERS_ORG_ID = -1;

const QUADRANT_STYLES = {
  star: {
    accent: '#16a34a',
    headerBg: 'linear-gradient(135deg, rgba(34,197,94,0.14) 0%, rgba(255,255,255,0.9) 55%)',
    border: 'rgba(22, 163, 74, 0.35)',
  },
  emerging: {
    accent: '#ca8a04',
    headerBg: 'linear-gradient(135deg, rgba(234,179,8,0.18) 0%, rgba(255,255,255,0.92) 55%)',
    border: 'rgba(202, 138, 4, 0.38)',
  },
  risk: {
    accent: '#ea580c',
    headerBg: 'linear-gradient(135deg, rgba(249,115,22,0.16) 0%, rgba(255,255,255,0.92) 55%)',
    border: 'rgba(234, 88, 12, 0.4)',
  },
  weak: {
    accent: '#dc2626',
    headerBg: 'linear-gradient(135deg, rgba(239,68,68,0.12) 0%, rgba(255,255,255,0.92) 55%)',
    border: 'rgba(220, 38, 38, 0.35)',
  },
} as const;

type QuadrantKey = keyof typeof QUADRANT_STYLES;

const QUADRANT_META: {
  key: QuadrantKey;
  title: string;
  emoji: string;
  blurb: string;
}[] = [
  {
    key: 'star',
    title: 'Star performers',
    emoji: '⭐',
    blurb: 'Above-median revenue in the range and positive growth vs comparison.',
  },
  {
    key: 'emerging',
    title: 'Emerging opportunities',
    emoji: '🚀',
    blurb: 'Below-median revenue but growing vs comparison — watchlist for scale.',
  },
  {
    key: 'risk',
    title: 'At risk',
    emoji: '⚠️',
    blurb: 'High revenue share but flat or negative growth — dependency / slowdown risk.',
  },
  {
    key: 'weak',
    title: 'Needs attention',
    emoji: '❌',
    blurb: 'Below-median revenue and flat or negative growth — prioritize turnaround.',
  },
];

function median(values: number[]): number {
  if (!values.length) {
    return 0;
  }
  const s = [...values].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 !== 0 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

function formatINRFull(value: number): string {
  return formatCurrencyWithSymbol(Number(value) || 0);
}

function formatGrowthPct(pct: number | null | undefined): string {
  if (pct == null || Number.isNaN(Number(pct))) {
    return '—';
  }
  const v = Number(pct);
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(0)}%`;
}

type QuadrantOrg = { name: string; revenue: number; growth: number | null };

function classifyQuadrant(revenue: number, revenueMid: number, growth: number): QuadrantKey {
  const highRev = revenue >= revenueMid;
  const posGrowth = growth > 0;
  if (highRev && posGrowth) {
    return 'star';
  }
  if (!highRev && posGrowth) {
    return 'emerging';
  }
  if (highRev && !posGrowth) {
    return 'risk';
  }
  return 'weak';
}

function buildQuadrantBuckets(rows: OrgRevenueRow[]): Record<QuadrantKey, QuadrantOrg[]> {
  const list = (rows || []).filter(r => r.organizationId !== OTHERS_ORG_ID);
  const empty: Record<QuadrantKey, QuadrantOrg[]> = { star: [], emerging: [], risk: [], weak: [] };
  if (!list.length) {
    return empty;
  }

  const revenues = list.map(r => Number(r.revenue?.mtd?.value) || 0);
  let revenueMid = median(revenues);
  const rMin = Math.min(...revenues);
  const rMax = Math.max(...revenues);
  if (rMax - rMin < 1e-9) {
    revenueMid = rMax > 0 ? rMax * 0.5 : 0;
  }

  for (const r of list) {
    const revenue = Number(r.revenue?.mtd?.value) || 0;
    const rawG = r.revenue?.mtd?.percentageChange;
    const growth = rawG != null && Number.isFinite(Number(rawG)) ? Number(rawG) : 0;
    const q = classifyQuadrant(revenue, revenueMid, growth);
    empty[q].push({
      name: resolvedOrganizationLabel(r.organizationId, r.organizationName),
      revenue,
      growth: rawG != null && Number.isFinite(Number(rawG)) ? Number(rawG) : null,
    });
  }

  (Object.keys(empty) as QuadrantKey[]).forEach(k => {
    empty[k].sort((a, b) => b.revenue - a.revenue);
  });

  return empty;
}

const MAX_ROWS = 10;

const QuadrantCard: FC<{
  meta: (typeof QUADRANT_META)[number];
  rows: QuadrantOrg[];
}> = ({ meta, rows }) => {
  const st = QUADRANT_STYLES[meta.key];
  return (
    <Paper
      elevation={0}
      sx={{
        ...reportChartPaperSx,
        p: 0,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: st.border,
        borderTop: `4px solid ${st.accent}`,
      }}>
      <Box sx={{ px: 1.5, py: 1.25, background: st.headerBg, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={800} sx={{ color: 'text.primary', lineHeight: 1.25 }}>
          {meta.emoji} {meta.title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35, lineHeight: 1.45 }}>
          {meta.blurb}
        </Typography>
      </Box>
      <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2 }}>
            No organizations in this quadrant.
          </Typography>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Organization</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">
                  Revenue
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 11 }} align="right">
                  Growth
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(0, MAX_ROWS).map((row, i) => {
                const g = row.growth;
                const growthColor =
                  g == null ? 'text.secondary' : g > 0 ? 'success.dark' : g < 0 ? 'error.dark' : 'text.secondary';
                return (
                  <TableRow key={`${meta.key}-${i}`} hover sx={{ '& td': { py: 0.65, fontSize: 12 } }}>
                    <TableCell sx={{ maxWidth: 0 }}>
                      <Tooltip title={row.name}>
                        <Typography noWrap fontWeight={600} color="text.primary">
                          {row.name}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {formatINRFull(row.revenue)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: growthColor }}>
                      {formatGrowthPct(g)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {rows.length > MAX_ROWS ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 1.5, py: 0.75, bgcolor: '#f8fafc' }}>
            +{rows.length - MAX_ROWS} more not shown
          </Typography>
        ) : null}
      </Box>
    </Paper>
  );
};

interface OrganizationHealthMatrixProps {
  rows: OrgRevenueRow[];
  loading?: boolean;
  comparisonMode?: ConsolidatedComparisonMode;
}

const OrganizationHealthMatrix: FC<OrganizationHealthMatrixProps> = ({
  rows,
  loading = false,
  comparisonMode = 'PRIOR_PERIOD',
}) => {
  const buckets = useMemo(() => buildQuadrantBuckets(rows), [rows]);

  const comparisonPhrase =
    comparisonMode === 'YOY' ? 'same calendar dates last year' : 'the immediately previous period of equal length';

  const quadrantInfoTooltip = (
    <Box component="span" sx={{ display: 'block', maxWidth: 340, fontSize: 12.5, lineHeight: 1.45, fontWeight: 400 }}>
      Each organization is placed using <strong>revenue in your selected date range</strong> (vs the portfolio median){' '}
      and <strong>growth %</strong> for that range vs {comparisonPhrase}. Use the cards to brief stakeholders without reading
      a scatter plot.
    </Box>
  );

  const quadrantTitleRow = (titleMb: number) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: titleMb }}>
      <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.25, color: 'text.primary' }} component="h2">
        Portfolio by quadrant
      </Typography>
      <Tooltip
        title={quadrantInfoTooltip}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: 'rgba(15, 23, 42, 0.92)',
              color: '#f1f5f9',
              maxWidth: 360,
              px: 1.25,
              py: 1,
              borderRadius: 1,
              '& strong': { color: '#fff', fontWeight: 700 },
            },
          },
          arrow: { sx: { color: 'rgba(15, 23, 42, 0.92)' } },
        }}>
        <InfoOutlinedIcon
          sx={{
            fontSize: 20,
            color: 'action.active',
            cursor: 'help',
            opacity: 0.85,
            flexShrink: 0,
            mt: 0.15,
            '&:hover': { opacity: 1 },
          }}
        />
      </Tooltip>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ mb: 3 }}>
        {quadrantTitleRow(1)}
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  const totalOrgs = (rows || []).filter(r => r.organizationId !== OTHERS_ORG_ID).length;
  if (totalOrgs === 0) {
    return (
      <Box sx={{ mb: 3 }}>
        {quadrantTitleRow(2)}
        <Paper
          elevation={0}
          sx={{
            ...reportChartPaperSx,
            py: 4,
            px: 2,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: '#fafbfc',
          }}>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480, mx: 'auto', lineHeight: 1.6 }}>
            No organizations in this view for the selected range. Adjust dates or organization filters, then search again.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      {quadrantTitleRow(2)}
      <Grid container spacing={2}>
        {QUADRANT_META.map(meta => (
          <Grid item xs={12} md={6} key={meta.key}>
            <QuadrantCard meta={meta} rows={buckets[meta.key]} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default OrganizationHealthMatrix;
